import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";

const bullets = [
    "Aligns learning to business outcomes and workforce plans",
    "Blends live cohorts, self-paced learning, cloud labs, and coaching",
    "Runs on a skills graph — not a catalog of courses",
    "AI-native personalization for every learner and admin",
    "Certified partner ecosystem across 28+ global bodies",
    "SLA-backed programs with executive-grade analytics",
];

const CapabilityDev = () => (
    <section data-testid="capability-section" className="sxe-section relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
            <div className="hero-orb w-[520px] h-[520px] bg-[#A100FF]/15 -bottom-40 -left-32" />
        </div>
        <div className="sxe-container relative">
            <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
                <div>
                    <div className="overline"><span className="dot" /> Enterprise Capability Development</div>
                    <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tighter leading-[1.05] text-white mt-5">
                        Turn learning into{" "}
                        <span className="text-gradient">enterprise capability</span>.
                    </h2>
                    <p className="text-[#B0A3C4] text-lg leading-relaxed mt-6 max-w-xl">
                        We combine deep instructional expertise with a modern platform to build enterprise capability that scales — not just courses that get completed.
                    </p>

                    <ul className="mt-9 space-y-3 max-w-xl">
                        {bullets.map((b, i) => (
                            <motion.li
                                key={i}
                                initial={{ opacity: 0, x: -12 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: i * 0.05 }}
                                className="flex items-start gap-3 text-[#E9CCFF]"
                                data-testid={`capability-bullet-${i}`}
                            >
                                <CheckCircle2 className="w-5 h-5 text-[#C266FF] shrink-0 mt-0.5" />
                                <span className="text-[15px] leading-relaxed">{b}</span>
                            </motion.li>
                        ))}
                    </ul>
                </div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="relative"
                >
                    <div className="sxe-card p-8 md:p-10 gradient-border">
                        <div className="text-[11px] uppercase tracking-widest text-[#75668C] font-display">Capability Framework</div>
                        <div className="mt-5 space-y-5">
                            {[
                                { level: "L4", label: "Applied Mastery", pct: 92 },
                                { level: "L3", label: "Practitioner", pct: 76 },
                                { level: "L2", label: "Proficient", pct: 58 },
                                { level: "L1", label: "Foundation", pct: 40 },
                            ].map((l) => (
                                <div key={l.level}>
                                    <div className="flex items-center justify-between text-xs text-[#B0A3C4] font-display">
                                        <span>{l.level} · {l.label}</span>
                                        <span className="font-mono text-[#E9CCFF]">{l.pct}%</span>
                                    </div>
                                    <div className="mt-1.5 h-2 rounded-full bg-white/[0.05] overflow-hidden">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            whileInView={{ width: `${l.pct}%` }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 1.2, ease: "easeOut" }}
                                            className="h-full bg-gradient-to-r from-[#7B00CC] to-[#C266FF]"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    </section>
);

export default CapabilityDev;
